const parentBlock = document.querySelector('.packs-block');
if (!parentBlock) return;

const sortedPackCombinations = packCombinations.sort((a, b) => a.packSize - b.packSize);

// Bra products have two variant option groups (band + cup). Their `[data-size]`
// labels carry just the band digits (e.g. "65") and `[data-cup]` labels carry
// just the cup letter (e.g. "B"); pack variant titles still contain the
// combined token (e.g. "Black / 65B"). Token matching needs to handle this.
const isBraProduct = !!document.querySelector('variant-radios[data-bra-options="true"]');

const sectionId = (
  document.querySelector('variant-radios') ||
  document.querySelector('variant-selects')
)?.dataset?.section;

// ─── State ──────────────────────────────────────────────────────────────────
// `displayedPackSize` is the pack size whose product is currently rendered in
// the product-info container. On page load it's the size of the product that was
// actually navigated to (1 = single). Each pack switch replaces the whole
// product-info container with the target pack's server-rendered section, so the
// inline DOM literally becomes that pack's PDP — colour swatches, size
// availability, price and CTA all come correct from the server. This removes the
// need for the previous hand-maintained incremental repaint/restore machinery.
let displayedPackSize = (function () {
  const cur = sortedPackCombinations.find((c) => c.isCurrent);
  return cur ? cur.packSize : 1;
}());
let currentSize = '';              // last band/size read from the variant radios
let currentCup = '';               // last cup (bra products only)
let preselectedPackSize = null;    // from ?pack= URL param on page load (legacy links)
let isRepainting = false;          // guards against overlapping repaints
let activeTriggerClass = null;     // unique Klaviyo trigger class currently on the BIS button
let triggerClassCounter = 0;       // incremented per reinit to produce a unique class

// Read URL param for pack preselection (backward-compat with existing ?pack= links)
(function initFromUrl() {
  const params = new URLSearchParams(window.location.search);
  const packParam = params.get('pack');
  if (packParam) preselectedPackSize = parseInt(packParam, 10);
})();

// ─── Toggle chrome helpers ────────────────────────────────────────────────────

function updateToggleStyle(isCurrentProduct, isAvailable) {
  const toggle = parentBlock.querySelector('.dropdown-toggle');
  if (!toggle) return;
  toggle.classList.remove('pack-selected', 'pack-unavailable-selected');
  if (!isCurrentProduct) {
    if (isAvailable) {
      toggle.classList.add('pack-selected');
    } else {
      toggle.classList.add('pack-unavailable-selected');
    }
  }
}

function updateToggleInfo(pack, variant) {
  const toggle = parentBlock.querySelector('.dropdown-toggle');
  if (!toggle) return;
  const savingEl = toggle.querySelector('.toggle-saving');
  const togglePriceEl = toggle.querySelector('.toggle-price');
  const toggleComparePriceEl = toggle.querySelector('.toggle-compare-price');
  const toggleSaleBadgeEl = toggle.querySelector('.toggle-sale-badge');

  if (!savingEl || !togglePriceEl || !toggleComparePriceEl || !toggleSaleBadgeEl) return;

  if (!pack || !variant) {
    savingEl.textContent = '';
    togglePriceEl.textContent = '';
    toggleComparePriceEl.textContent = '';
    toggleSaleBadgeEl.classList.add('hidden');
    return;
  }

  const onSale = variant.on_sale === true || variant.on_sale === 'true';
  const saving = typeof variant.saving_percentage !== 'undefined' ? Number(variant.saving_percentage) : 0;
  savingEl.textContent = saving > 0 ? (saving + packsI18n.percentOff) : (onSale ? packsI18n.onSale : '');
  togglePriceEl.textContent = variant.price || '';
  toggleComparePriceEl.textContent = variant.compare_at_price || '';
  toggleComparePriceEl.classList.toggle('hidden', !onSale);
  toggleSaleBadgeEl.classList.toggle('hidden', !onSale);
  savingEl.classList.toggle('hidden', onSale);
}

// ─── Full product-info repaint ────────────────────────────────────────────────

function buildProductUrl(productUrl, variantId) {
  let url;
  try { url = new URL(productUrl, window.location.origin); }
  catch (_) { return productUrl; }
  if (variantId) url.searchParams.set('variant', variantId);
  return url.pathname + url.search;
}

// Hard fallback: if the section fetch fails we simply navigate to the target
// product so the user still ends up on the right pack/colour.
function navigateFallback(productUrl, variantId) {
  window.location.href = buildProductUrl(productUrl, variantId);
}

// Fetch the target product's main-product section and swap it into the live
// `#ProductInfo-<sectionId>` container. The packs dropdown lives *inside* that
// container, so we detach the live `.packs-block` node first (keeping all its
// listeners + state) and re-insert it over the freshly-rendered placeholder.
function repaintInfoContainer(productUrl, variantId, productId) {
  if (!sectionId) { navigateFallback(productUrl, variantId); return; }
  const container = document.getElementById('ProductInfo-' + sectionId);
  if (!container) { navigateFallback(productUrl, variantId); return; }

  isRepainting = true;
  showRepaintLoading();

  // Fetch the FULL product page (not ?section_id) so the response also carries
  // the <head> — we sync the title + canonical/social meta from it. The full page
  // still contains `#ProductInfo-<sectionId>` and the inline data scripts, so the
  // body swap works exactly as with a section fetch (one request, not two).
  const fetchUrl = new URL(productUrl, window.location.origin);
  if (variantId) fetchUrl.searchParams.set('variant', variantId);

  fetch(fetchUrl.pathname + fetchUrl.search)
    .then(function (r) { return r.text(); })
    .then(function (html) {
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const incoming = parsed.getElementById('ProductInfo-' + sectionId);
      if (!incoming) throw new Error('packs: no product-info container in fetched section');

      // Refresh the page-load product-data globals (`variantStock`,
      // `connectedProductVariantData`) to the target product's values BEFORE the
      // swap. The new variant-radios upgrades synchronously during innerHTML and
      // immediately runs onVariantChange → updateInventoryNote, which looks the
      // current variant up in `variantStock`; if it still holds the old product's
      // ids the lookup is undefined and global.js throws, aborting the handler
      // (leaving the CTA stuck disabled / "Unavailable").
      refreshProductDataGlobals(parsed);

      // `#option-map` lives outside the swapped container, so update it too — the
      // new variant-radios reads it (for colour-swatch size detection) on upgrade.
      const incomingOptionMap = parsed.getElementById('option-map');
      const liveOptionMap = document.getElementById('option-map');
      if (incomingOptionMap && liveOptionMap) liveOptionMap.textContent = incomingOptionMap.textContent;

      // Detach the live dropdown so the innerHTML swap can't destroy it.
      const liveDropdown = parentBlock;
      liveDropdown.remove();

      // Swap. Custom elements in the new markup (variant-radios, product-form,
      // color-swatches, size-guide…) upgrade synchronously here and re-bind
      // themselves; variant-radios also re-dispatches `colorSwatchesUpdated`.
      container.innerHTML = incoming.innerHTML;

      const freshDropdown = container.querySelector('.packs-block');
      if (freshDropdown) {
        freshDropdown.replaceWith(liveDropdown);
      } else {
        container.appendChild(liveDropdown);
      }

      // Correct the URL to the new product. global.js's updateURL ran during the
      // swap and resolved ?variant=&size= for the selected variant, but on the OLD
      // pathname (it only rewrites search params). Preserve the variant it settled
      // on (read from the form input) AND the size, swapping only the pathname.
      // Dropping ?size here would break bras: their radios are re-selected from
      // ?size on the next swap (the server doesn't pre-check bra radios).
      const target = new URL(productUrl, window.location.origin);
      const settled = new URL(window.location.href);
      const settledForm = document.getElementById('product-form-' + sectionId);
      const settledIdInput = settledForm && settledForm.querySelector('input[name="id"]');
      const resolvedVariantId = (settledIdInput && settledIdInput.value) || variantId;
      if (resolvedVariantId) target.searchParams.set('variant', resolvedVariantId);
      const settledSize = settled.searchParams.get('size');
      if (settledSize) target.searchParams.set('size', settledSize);
      window.history.replaceState({}, '', target.pathname + target.search);

      // Sync <head> (title + canonical/social meta) so analytics, sharing and the
      // browser tab reflect the displayed product instead of the page-load one.
      updateHeadFromParsed(parsed);

      reinitAfterRepaint(productId, variantId);
      isRepainting = false;
      hideRepaintLoading();
    })
    .catch(function () {
      isRepainting = false;
      hideRepaintLoading();
      navigateFallback(productUrl, variantId);
    });
}

// Loading state shown over the product info while a pack switch is in flight. It
// gives visual feedback AND prevents adding the WRONG product: until the section
// swap lands, the previous product's form is still live. The overlay blocks
// direct clicks on the in-form Add button, and we set aria-disabled on the Add
// button(s) so a programmatic click (e.g. the sticky ATC delegating to it) is
// skipped by product-form.js too. The swap replaces these buttons, clearing it.
function showRepaintLoading() {
  const wrapper = document.querySelector('.product__info-wrapper');
  if (wrapper) {
    if (window.getComputedStyle(wrapper).position === 'static') wrapper.style.position = 'relative';
    if (!wrapper.querySelector('.packs-repaint-overlay')) {
      const overlay = document.createElement('div');
      overlay.className = 'packs-repaint-overlay';
      overlay.setAttribute('aria-hidden', 'true');
      overlay.innerHTML = '<div class="packs-repaint-spinner"></div>';
      wrapper.appendChild(overlay);
    }
  }
  if (sectionId) {
    const form = document.getElementById('product-form-' + sectionId);
    if (form) {
      form.querySelectorAll('[name="add"]').forEach(function (b) { b.setAttribute('aria-disabled', 'true'); });
    }
  }
}

function hideRepaintLoading() {
  const overlay = document.querySelector('.product__info-wrapper .packs-repaint-overlay');
  if (overlay) overlay.remove();
}

// Re-run the inline data scripts from the fetched section so the page-load
// globals match the target product. These `<script>`s don't execute when
// inserted via innerHTML, and one of them (`connectedProductVariantData`) lives
// outside the swapped container entirely. We execute them by appending a new
// <script> (inline scripts run synchronously on insertion), neutralising the
// top-level `let connectedProductVariantData` declaration to a reassignment so
// re-running can't throw "already declared" against the existing global.
// `variantStock` is declared with `var`, which is safe to re-declare.
function refreshProductDataGlobals(parsedDoc) {
  const scripts = parsedDoc.querySelectorAll('script:not([src]):not([type])');
  let code = '';
  scripts.forEach(function (s) {
    const txt = s.textContent || '';
    if (txt.indexOf('connectedProductVariantData') !== -1 && txt.indexOf('.push(') !== -1) {
      code += '\n' + txt.replace(/\b(let|const|var)\s+connectedProductVariantData\b/, 'connectedProductVariantData') + '\n;';
    } else if (txt.indexOf('variantStock') !== -1 && txt.indexOf('.push(') !== -1) {
      code += '\n' + txt + '\n;';
    }
  });
  if (!code) return;
  try {
    const runner = document.createElement('script');
    runner.textContent = code;
    document.body.appendChild(runner);
    runner.remove();
  } catch (_) { /* a bad data script must not abort the repaint */ }
}

// Sync the document <head> to the fetched product page: title, canonical link,
// description, all og:*/twitter:* social meta, and the Product JSON-LD. Keeps
// analytics/sharing/tab in step with the displayed pack (the body swap only
// touches #ProductInfo, never <head>). Wrapped so a head hiccup can't break the
// repaint.
function updateHeadFromParsed(parsedDoc) {
  try {
    if (!parsedDoc || !parsedDoc.head || !document.head) return;
    const head = document.head;
    const incoming = parsedDoc.head;

    const titleEl = incoming.querySelector('title');
    if (titleEl) document.title = titleEl.textContent;

    const selectors = ['link[rel="canonical"]', 'meta[name="description"]'];
    incoming.querySelectorAll('meta[property]').forEach(function (el) {
      const p = el.getAttribute('property');
      if (p) selectors.push('meta[property="' + p + '"]');
    });
    incoming.querySelectorAll('meta[name^="twitter:"]').forEach(function (el) {
      const n = el.getAttribute('name');
      if (n) selectors.push('meta[name="' + n + '"]');
    });

    const done = {};
    selectors.forEach(function (sel) {
      if (done[sel]) return;
      done[sel] = true;
      const incomingEls = incoming.querySelectorAll(sel);
      if (!incomingEls.length) return;
      // Replace the live set for this key with the incoming set.
      head.querySelectorAll(sel).forEach(function (el) { el.remove(); });
      incomingEls.forEach(function (el) { head.appendChild(el.cloneNode(true)); });
    });

    // Product JSON-LD schema (if present in <head>).
    function findProductLd(root) {
      return Array.prototype.slice.call(root.querySelectorAll('script[type="application/ld+json"]'))
        .find(function (s) { return /"@type"\s*:\s*"Product"/.test(s.textContent || ''); });
    }
    const incomingLd = findProductLd(incoming);
    if (incomingLd) {
      const liveLd = findProductLd(head);
      if (liveLd) liveLd.textContent = incomingLd.textContent;
      else head.appendChild(incomingLd.cloneNode(true));
    }
  } catch (_) { /* head sync must never break the repaint */ }
}

// Re-bind everything that doesn't auto-upgrade after a container swap.
function reinitAfterRepaint(productId, variantId) {
  // Re-render the dropdown option list against the now-current variant selection.
  const sel = readCheckedVariantSelection();
  currentCup = sel.cup;
  filterPacksByColorAndSize(sortedPackCombinations, currentColor, sel.size, '.pack-dropdown-content');

  // Re-init Klaviyo BIS for the now-displayed product.
  reinitKlaviyoForProduct(productId, variantId);

  // Let the other product-info features (sticky ATC, accordions, co2 chips…)
  // re-bind to the freshly-swapped DOM.
  window.dispatchEvent(new CustomEvent('product-info-repainted', { detail: { sectionId: sectionId } }));
}

// Klaviyo BIS is initialised per-product at page load, so after switching to a
// different product (every pack/single is its own product) the page-load instance
// doesn't know the new product's variants — clicking the wishlist button then
// throws "Cannot read properties of undefined (reading 'id')" because the URL's
// variant isn't in Klaviyo's data. So we must re-init Klaviyo for the displayed
// product on each repaint. We re-init even when the current size is in stock (the
// button is hidden), so the instance already matches the product if the user then
// picks an out-of-stock size (a same-product size change does NOT repaint).
function reinitKlaviyoForProduct(productId, variantId) {
  if (!sectionId || typeof window.reinitKlaviyoBIS !== 'function') return;
  const form = document.getElementById('product-form-' + sectionId);
  if (!form) return;
  const bisBtn = form.querySelector('.klaviyo-bis-trigger');
  if (!bisBtn) return; // Klaviyo BIS not configured for this product

  // Clone the button to strip the dead click handler left by the previous
  // (now-destroyed) Klaviyo instance, and drop the page-load `klaviyo-bis-trigger-ob`
  // class so that instance's observer can't re-attach a stale handler (that combo —
  // dead handler + removed iframe — is what threw "...null (reading 'document')").
  // The new instance gets a unique trigger class that only it watches.
  const fresh = bisBtn.cloneNode(true);
  bisBtn.parentNode.replaceChild(fresh, bisBtn);
  fresh.classList.remove('klaviyo-bis-trigger-ob');
  if (activeTriggerClass) fresh.classList.remove(activeTriggerClass);
  const triggerClass = 'klaviyo-bis-trigger-ob-' + (++triggerClassCounter);
  fresh.classList.add(triggerClass);
  activeTriggerClass = triggerClass;

  const input = form.querySelector('input[name="id"]');
  const vId = (input && input.value) || variantId;
  // forceShow:false — the server render already set the button's visibility.
  window.reinitKlaviyoBIS({
    variantId: vId,
    productId: productId,
    triggerClass: triggerClass,
    forceShow: false,
  });
}

// ─── Dropdown toggle (open/close) ─────────────────────────────────────────────

parentBlock.querySelector('.dropdown-toggle').addEventListener('click', function () {
  parentBlock.querySelector('.packs-dropdown').classList.toggle('active');
  if (parentBlock.querySelector('.packs-dropdown').classList.contains('active')) {
    parentBlock.querySelector('.dropdown-toggle').setAttribute('aria-expanded', 'true');
  } else {
    parentBlock.querySelector('.dropdown-toggle').setAttribute('aria-expanded', 'false');
  }
});

// Close when clicking outside the packs block entirely.
document.addEventListener('click', function (event) {
  if (parentBlock.contains(event.target)) return;
  const dropdown = parentBlock.querySelector('.packs-dropdown');
  if (!dropdown.classList.contains('active')) return;
  dropdown.classList.remove('active');
  parentBlock.querySelector('.dropdown-toggle').setAttribute('aria-expanded', 'false');
});

// Mark dropdown as having been interacted with (once only)
parentBlock.addEventListener('click', function handleFirstClick() {
  parentBlock.querySelector('.packs-dropdown').classList.add('clicked');
  parentBlock.removeEventListener('click', handleFirstClick);
});

// ─── Core filter + render ─────────────────────────────────────────────────────

// Exact token match for sizes: splits variant title on "/" and whitespace so that
// "xs" does not match "xxs", and "s" does not match "xs" or "xxs". For bras the
// option2 token is band+cup combined (e.g. "65B"), so we match against `band+cup`.
function titleMatchesSize(title, size, cup) {
  if (!size && !cup) return true;
  const tokens = title.toLowerCase().split(/[\s/]+/).map(function (t) { return t.trim(); }).filter(Boolean);
  if (isBraProduct) {
    if (size && cup) return tokens.includes((size + cup).toLowerCase());
    if (size) return tokens.some(function (t) { return /^\d+\D+$/.test(t) && t.indexOf(size.toLowerCase()) === 0; });
    if (cup) return tokens.some(function (t) { return /^\d+\D+$/.test(t) && t.replace(/[0-9]/g, '') === cup.toLowerCase(); });
    return true;
  }
  return tokens.includes(size.toLowerCase());
}

function filterPacksByColorAndSize(packs, currentColorArg, variantSize, containerOrSelector) {
  const color = (currentColorArg || '').toLowerCase();
  const size = (variantSize || '').toLowerCase();
  const cup = (currentCup || '').toLowerCase();
  currentSize = variantSize || '';
  // A "fully selected" variant — for bras requires both band and cup. Until
  // then we render the dropdown but suppress availability indicators.
  const fullyVariantSelected = isBraProduct ? (!!size && !!cup) : !!size;
  const mappedPacks = packs.map(function (pack) {
    const sizeColorMatches = !fullyVariantSelected ? [] : pack.variants.filter(function (v) {
      const t = (v.title || '').toLowerCase();
      const matchesColor = (color && pack.packSize > 1) ? t.includes(color) : true;
      const matchesSize = titleMatchesSize(t, size, cup);
      return matchesColor && matchesSize;
    });

    // Fallback: color-only (so the pack still appears when size isn't selected)
    const colorOnlyMatches = pack.variants.filter(function (v) {
      const t = (v.title || '').toLowerCase();
      return (color && pack.packSize > 1) ? t.includes(color) : true;
    });

    const displayVariant = sizeColorMatches.length > 0
      ? sizeColorMatches[0]
      : (colorOnlyMatches.length > 0 ? colorOnlyMatches[0] : null);

    const isAvailableForSizeColor = sizeColorMatches.length > 0 &&
      sizeColorMatches.some(function (v) { return v.available === true; });

    return {
      title: pack.title,
      displayName: pack.displayName,
      packSize: pack.packSize,
      productId: pack.productId,
      productUrl: pack.productUrl,
      isCurrent: pack.isCurrent,
      variants: pack.variants,
      displayVariant: displayVariant,
      isAvailableForSizeColor: isAvailableForSizeColor,
      fullyVariantSelected: fullyVariantSelected
    };
  });

  // Hide the block only when there are no packs with packSize > 1
  const hasPacks = mappedPacks.some(function (p) { return p.packSize > 1; });
  if (hasPacks) {
    parentBlock.classList.remove('hidden');
  } else {
    parentBlock.classList.add('hidden');
  }

  if (containerOrSelector) {
    generateVariantDropdown(mappedPacks, variantSize, containerOrSelector);
  }

  return mappedPacks;
}

function generateVariantDropdown(mappedPacks, variantSize, containerOrSelector) {
  if (!Array.isArray(mappedPacks)) return;

  const container = (typeof containerOrSelector === 'string')
    ? parentBlock.querySelector(containerOrSelector)
    : containerOrSelector;
  if (!container) return;

  const template = document.getElementById('dropdown-option-template');
  if (!template) return;

  container.innerHTML = '';

  function buildUrlWithSize(baseUrl, sizeValue) {
    if (!baseUrl) return '#';
    let absolute;
    try { absolute = new URL(baseUrl); }
    catch (_) { absolute = new URL(baseUrl, window.location.origin); }
    if (sizeValue) absolute.searchParams.set('size', String(sizeValue));
    return baseUrl.startsWith('/') ? (absolute.pathname + absolute.search + absolute.hash) : absolute.toString();
  }

  let highestSaving = 0;

  mappedPacks.forEach(function (pack) {
    const variant = pack.displayVariant;

    const clone = template.content.cloneNode(true);
    const optionEl = clone.querySelector('.dropdown-option');

    const nameNode = optionEl.querySelector('.option-name');
    const saleNode = optionEl.querySelector('.sale-badge');
    const priceNode = optionEl.querySelector('.option-price');
    const discNode = optionEl.querySelector('.discount-badge');
    const compareNode = optionEl.querySelector('.compare-price');

    // Name
    nameNode.textContent = pack.displayName;

    // Price
    priceNode.textContent = variant ? (variant.price || '') : '';

    // On Sale replaces % saving badge
    const onSale = variant && (variant.on_sale === true || variant.on_sale === 'true');
    const saving = variant && typeof variant.saving_percentage !== 'undefined'
      ? Number(variant.saving_percentage) : 0;

    if (onSale) {
      saleNode.textContent = packsI18n.onSale;
      if (compareNode && variant.compare_at_price) {
        optionEl.classList.add('on-sale');
        compareNode.textContent = variant.compare_at_price;
      }
    }

    if (saving > 0) {
      discNode.textContent = saving + packsI18n.percentOff;
      optionEl.classList.add('has-discount');
      if (saving > highestSaving) highestSaving = saving;
    }

    // Out-of-stock state — only indicate availability once the user has fully
    // selected a variant (for bras: both band and cup).
    if (pack.fullyVariantSelected && !pack.isAvailableForSizeColor) {
      optionEl.classList.add('is-unavailable');
    }

    // Selected / current state reflects the product currently rendered inline.
    if (pack.packSize === displayedPackSize) optionEl.classList.add('is-selected');

    // Data attributes
    optionEl.dataset.packSize = pack.packSize;
    optionEl.dataset.productId = pack.productId;

    // URL (used for accessibility / fallback)
    const baseUrl = variant ? (variant.base_url || pack.productUrl) : (pack.productUrl || '#');
    const finalUrl = buildUrlWithSize(baseUrl, variantSize);
    optionEl.dataset.targetUrl = finalUrl;

    optionEl.setAttribute('role', 'button');
    optionEl.setAttribute('tabindex', '0');
    optionEl.style.cursor = 'pointer';

    // ── Click handler: switch product by repainting the info container ───────
    const activate = function () {
      parentBlock.querySelector('.packs-dropdown').classList.remove('active');
      parentBlock.querySelector('.dropdown-toggle').setAttribute('aria-expanded', 'false');

      if (isRepainting) return;                 // ignore clicks mid-swap
      if (pack.packSize === displayedPackSize) return; // already showing this product

      // Optimistic toggle update for snappiness; reinitAfterRepaint re-syncs it
      // from the freshly-rendered data once the swap lands.
      displayedPackSize = pack.packSize;
      const isSingle = pack.packSize === 1;
      const selectedOptionEl = parentBlock.querySelector('.selected-option');
      if (selectedOptionEl) selectedOptionEl.textContent = pack.displayName;
      updateToggleInfo(isSingle ? null : pack, isSingle ? null : variant);
      updateToggleStyle(isSingle, pack.fullyVariantSelected ? pack.isAvailableForSizeColor : true);
      container.querySelectorAll('.dropdown-option').forEach(function (el) {
        el.classList.toggle('is-selected', parseInt(el.dataset.packSize, 10) === pack.packSize);
      });

      repaintInfoContainer(pack.productUrl, variant ? variant.id : null, pack.productId);
    };

    optionEl.addEventListener('click', activate);
    optionEl.addEventListener('keydown', function (ev) {
      if (ev.key === 'Enter' || ev.key === ' ' || ev.key === 'Spacebar') {
        ev.preventDefault();
        activate();
      }
    });

    container.appendChild(optionEl);
  });

  // Update the "Save up to X% with packs" title
  const savingNode = document.querySelector('.dropdown-title .saving-percentage');
  if (savingNode) {
    if (highestSaving > 0) {
      savingNode.textContent = highestSaving + '%';
      savingNode.style.display = '';
    } else {
      savingNode.textContent = '';
      savingNode.style.display = 'none';
    }
  }

  // Sync toggle chrome + selected-option text to the displayed pack.
  const displayedPack = mappedPacks.find(function (p) { return p.packSize === displayedPackSize; });
  const selectedOptionEl = parentBlock.querySelector('.selected-option');
  if (displayedPack) {
    if (selectedOptionEl) selectedOptionEl.textContent = displayedPack.displayName;
    const isSingle = displayedPack.packSize === 1;
    updateToggleInfo(isSingle ? null : displayedPack, isSingle ? null : displayedPack.displayVariant);
    const avail = displayedPack.fullyVariantSelected ? displayedPack.isAvailableForSizeColor : true;
    updateToggleStyle(isSingle, avail);
  }

  // Apply preselected pack from URL params (runs once, on first render).
  if (preselectedPackSize !== null) {
    const toPreselect = mappedPacks.find(function (p) { return p.packSize === preselectedPackSize; });
    preselectedPackSize = null; // clear regardless
    if (toPreselect && toPreselect.packSize !== displayedPackSize) {
      const optionToClick = container.querySelector('[data-pack-size="' + toPreselect.packSize + '"]');
      if (optionToClick) optionToClick.click();
    }
  }
}

// ─── Event listener ───────────────────────────────────────────────────────────

function readCheckedVariantSelection() {
  // For bras, event.detail from updateColorSwatches is the combined "65B" only
  // when both band and cup are checked; otherwise it's empty. The band radio's
  // value alone is "65" (just digits), which can't match the combined token in
  // pack variant titles — so we always read both groups directly.
  if (isBraProduct) {
    const checkedBand = document.querySelector('input[type="radio"][name="Size"]:checked');
    const checkedCup = document.querySelector('input[type="radio"][name="Cup"]:checked');
    return {
      size: checkedBand ? checkedBand.value : '',
      cup: checkedCup ? checkedCup.value : ''
    };
  }
  const checkedSize = document.querySelector('input[type="radio"][name="Size"]:checked');
  return {
    size: checkedSize ? checkedSize.value : '',
    cup: ''
  };
}

// Fires on size/colour changes within the currently displayed product. We only
// rebuild the dropdown option list (prices/availability for the new selection) —
// switching products is driven solely by option clicks (→ repaintInfoContainer).
window.addEventListener('colorSwatchesUpdated', function (event) {
  const sel = readCheckedVariantSelection();
  let variantSize = isBraProduct ? sel.size : (event.detail || sel.size);
  currentCup = sel.cup;
  filterPacksByColorAndSize(sortedPackCombinations, currentColor, variantSize, '.pack-dropdown-content');
});

// Initial render: populate the dropdown immediately so all bundles are visible
// from the start, even on products that don't dispatch `colorSwatchesUpdated`
// on load. Deferred past obScripts/global.js's customElements.define so
// VariantRadios has had a chance to upgrade the DOM.
setTimeout(function () {
  const sel = readCheckedVariantSelection();
  currentCup = sel.cup;
  filterPacksByColorAndSize(sortedPackCombinations, currentColor, sel.size, '.pack-dropdown-content');
}, 0);
